import React, { useContext } from 'react';
import { PermissionTypesEnum, VisitorRecordDetailOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import { Card, TwoColGrid } from '@components/ringil3/styles.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Col } from '@components/styles.tsx';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgUsers from '@icons/users-inverted.svg?react';
import SvgInfo from '@icons/info.svg?react';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3.tsx';
import { useVisitorSidebar } from '@app/pages/timeslots/sidebars/VisitorSidebar.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  data: VisitorRecordDetailOutDTO;
  loadData?: () => void;
}

const Visitdetail: React.FC<ComponentProps> = ({ data, loadData }) => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const { VisitorSidebar, setVisible: setVisibleVisitorSidebar, visible: visibleVisitorSidebar } = useVisitorSidebar();

  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE]);

  return (
    <>
      <Card>
        <TwoColGrid>
          <Col className={'gap-4'}>
            <COHeadline title={tr(`VisitDetail.mainContact`, `Main contact`)} variant={'h4'} icon={<SvgUser />} />
            <div className={'gap-2'}>
              <span className="font-sans font-medium text-base text-content-primary">{data.visitorName}</span>
              <div className="font-sans font-normal text-content-primary text-sm leading-normal">
                <div>{data.visitorId}</div>
                <div>{data.visitorCompany}</div>
                {data.car && (
                  <div>
                    {tr('VisitDetail.carPlate', 'Car plate: {{carPlate}}', { carPlate: data.car.carPlate })}
                  </div>
                )}
              </div>
            </div>
            <div className={'gap-2'}>
              <COHeadline title={tr(`VisitDetail.mainResponsiblePerson`, `Responsible person`)} variant={'h4'}
                          icon={<SvgUser />} />
              <span className="font-sans text-sm font-normal text-content-primary">{data.responsiblePersonName}</span>
            </div>
            <div className={'flex flex-row gap-2 items-center'}>
              <span className="text-content-tertiary"><SvgInfo /></span>
              <span className="font-sans text-sm font-normal text-content-primary">{data.note}</span>
            </div>
          </Col>
          <Col className={'gap-4'}>
            <EditableHeading3
              title={tr(`VisitDetail.additionalContacts`, `Additional contacts`)}
              icon={<SvgUsers />}
              editable={editEnabled}
              titleVariant={'h4'}
              onEditClick={() => setVisibleVisitorSidebar(true)}
            />
            {data.otherGuests && data.otherGuests.length > 0 ? (
              <div className="flex flex-col gap-3">
                {data.otherGuests.map((guest, index) => (
                  <div key={index} className={'gap-2'}>
                    <span className="font-sans font-medium text-base text-content-primary">{guest.name}</span>
                    <div
                      className="font-sans font-normal text-content-secondary text-sm leading-normal">{guest.visitorId}</div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="font-sans text-sm font-normal text-content-gr4">{t('global.noRecordFound')}</span>
            )}
          </Col>
        </TwoColGrid>
      </Card>
      {visibleVisitorSidebar && (
        <VisitorSidebar
          visible={visibleVisitorSidebar}
          setVisible={setVisibleVisitorSidebar}
          item={{
            duplicate: false,
            record: data,
          }}
          onContinue={() => {
            loadData();
            setVisibleVisitorSidebar(false);
          }}
        />
      )}
    </>
  );
};

export default Visitdetail;
